import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { allNavItems, NavItem } from "../../seeds";

const NavSearch: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const results = useMemo((): NavItem[] => {
    const q = query.trim().toLowerCase();
    if (!q) return allNavItems;
    return allNavItems.filter(
      (item) => item.name.toLowerCase().includes(q) || item.groupLabel?.toLowerCase().includes(q)
    );
  }, [query]);

  const open = useCallback(() => {
    setIsOpen(true);
    setQuery("");
    setActiveIndex(0);
  }, []);

  // Plain close — used for an outside click, where the admin's intent was to
  // interact with whatever they clicked. Stealing focus back to the trigger
  // here would fight that click (e.g. if they clicked into a different
  // input elsewhere on the page).
  const close = useCallback(() => setIsOpen(false), []);

  // Close + return focus to the trigger — for the cases where the focused
  // <input> is about to be unmounted and there's nothing else the admin was
  // trying to focus instead (Escape, or having just selected a result).
  const closeAndRefocus = useCallback(() => {
    close();
    triggerRef.current?.focus();
  }, [close]);

  const toggle = useCallback(
    () => (isOpen ? closeAndRefocus() : open()),
    [isOpen, closeAndRefocus, open]
  );

  const goTo = (item: NavItem) => {
    navigate(item.path);
    closeAndRefocus();
  };

  // Global shortcut — Ctrl/Cmd+K toggles search from anywhere in the admin
  // panel, matching every other menu in this nav (re-triggering closes it).
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, [toggle]);

  // Focus the input the moment the panel opens.
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Reset the highlighted result whenever the result set changes, so it
  // never points past the end of a shorter, newly-filtered list.
  useEffect(() => {
    setActiveIndex(0);
  }, [results.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, close]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      closeAndRefocus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIndex]) goTo(results[activeIndex]);
    }
  };

  return (
    <div className="top-navbar-search" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className="top-navbar-search-trigger"
        onClick={toggle}
        title="Search menu (Ctrl/Cmd+K)"
      >
        <i className="fa-solid fa-magnifying-glass" />
        <span className="top-navbar-search-hint">Search menu…</span>
        <span className="top-navbar-search-kbd">⌘K</span>
      </button>

      {isOpen && (
        <div className="top-navbar-search-panel">
          <div className="top-navbar-search-input-wrap">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search menu items…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="top-navbar-search-results">
            {results.length === 0 ? (
              <div className="top-navbar-dropdown-empty">No menu items match "{query}"</div>
            ) : (
              results.map((item, index) => (
                <div
                  key={item.path}
                  // Not an independent tab stop: keyboard access is via the
                  // input's own arrow-key + Enter handling above (standard
                  // for a search-with-live-results widget) — adding a second,
                  // separately-focusable-but-unhandled path here would be a
                  // dead end for a keyboard user who tabs directly to a row.
                  className={`top-navbar-search-result ${index === activeIndex ? "active" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => goTo(item)}
                >
                  <span>{item.name}</span>
                  {item.groupLabel && <span className="top-navbar-search-group">{item.groupLabel}</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavSearch;
